import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const footerCategoriesLeft = categories.slice(0, 7);
const footerCategoriesRight = categories.slice(7);

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const websiteAlt = (formData.get('website_alt') as string || '').trim();
    if (websiteAlt) {
      setSubmitted(true);
      setEmail('');
      return;
    }
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('email', email.trim());
      await fetch('https://readdy.ai/api/form/d92hmtg0sqa2sn3dngt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-primary-500 rounded-t-[2.5rem] mx-3 md:mx-5 mt-8 md:mt-12 overflow-hidden">
      <div className="px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link
              to="/"
              className="font-heading text-2xl md:text-3xl font-bold text-background-50 block mb-6"
            >
              GTBuy
              <br />
              Spreadsheet
            </Link>
            <p className="text-background-50/50 text-xs leading-relaxed max-w-xs">
              The #1 comparison and automation resource for buying agents. Helping sourcing
              professionals make informed decisions since 2024.
            </p>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Categories</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="space-y-2">
                {footerCategoriesLeft.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="space-y-2">
                {footerCategoriesRight.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Quick Links</h4>
            <div className="space-y-2.5">
              <Link
                to="/"
                className="block text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200"
              >
                Home
              </Link>
              <Link
                to="/what-is-gtbuy-spreadsheet"
                className="block text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200"
              >
                What is GTBuy Spreadsheet
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200"
              >
                gtbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 text-background-50/60 text-xs hover:text-background-50 transition-colors duration-200"
              >
                buy it now
                <i className="ri-external-link-line text-[10px]"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Stay Updated</h4>
            <p className="text-background-50/50 text-xs mb-4 leading-relaxed">
              Get the latest spreadsheet automation tips and GTBuy updates delivered to your inbox.
            </p>
            {submitted ? (
              <div className="flex items-center gap-2 text-accent-500 text-sm">
                <i className="ri-checkbox-circle-fill"></i>
                <span>Thanks for subscribing!</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2" data-readdy-form>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-50/40 focus:outline-none focus:border-background-50/40 transition-all"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-10 h-10 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center hover:bg-accent-600 transition-all duration-200 flex-shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-arrow-right-line"></i>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-background-50/10 px-6 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-background-50/40 text-xs">
          &copy; {new Date().getFullYear()} GTBuy Spreadsheet. All external links open in new tabs.
        </p>
        <div className="flex items-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-background-50/40 text-xs hover:text-background-50/70 transition-colors"
          >
            KASpreadsheet
          </a>
          <Link
            to="/what-is-gtbuy-spreadsheet"
            className="text-background-50/40 text-xs hover:text-background-50/70 transition-colors"
          >
            SEO Guide
          </Link>
        </div>
      </div>

      <div className="px-6 md:px-10 pb-8 overflow-hidden select-none">
        <p className="font-heading text-[12vw] md:text-[10vw] font-bold text-background-50/[0.03] whitespace-nowrap leading-none">
          GTBUY
        </p>
      </div>
    </footer>
  );
}